import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Sparkles, X } from "lucide-react";
import ReactMarkdown from "react-markdown";

interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  description: string;
  category: string;
  importance: string;
  publishedAt: string;
}

interface WhatsNewDialogProps {
  open: boolean;
  onClose: () => void;
  changelogs: ChangelogEntry[];
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead: () => void;
}

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'FEATURE':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'IMPROVEMENT':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'BUGFIX':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    case 'ANNOUNCEMENT':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  }
};

const getCategoryLabel = (category: string) => {
  switch (category) {
    case 'FEATURE':
      return 'Nowa funkcja';
    case 'IMPROVEMENT':
      return 'Ulepszenie';
    case 'BUGFIX':
      return 'Naprawa';
    case 'ANNOUNCEMENT':
      return 'Ogłoszenie';
    default:
      return category;
  }
};

export const WhatsNewDialog: React.FC<WhatsNewDialogProps> = ({
  open,
  onClose,
  changelogs,
  onMarkAsRead,
  onMarkAllAsRead,
}) => {
  const handleClose = () => {
    // Mark all as read when closing
    onMarkAllAsRead();
    onClose();
  };

  if (changelogs.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-primary" />
              <div>
                <DialogTitle className="text-2xl">Co nowego w systemie?</DialogTitle>
                <DialogDescription>
                  Poznaj najnowsze funkcje i ulepszenia
                </DialogDescription>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {changelogs.map((changelog) => (
            <div key={changelog.id} className="border rounded-lg p-6 bg-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getCategoryColor(changelog.category)}>
                      {getCategoryLabel(changelog.category)}
                    </Badge>
                    <Badge variant="outline">v{changelog.version}</Badge>
                  </div>
                  <h3 className="text-xl font-bold text-foreground">{changelog.title}</h3>
                </div>
              </div>

              <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{changelog.description}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button onClick={handleClose} className="flex items-center gap-2">
            Rozumiem
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
